import {
  ApiBlueprint,
  configApiRef,
  createApiFactory,
  createFrontendModule,
  discoveryApiRef,
  identityApiRef,
} from '@backstage/frontend-plugin-api';
import {
  techInsightsApiRef,
  TechInsightsClient,
  jsonRulesEngineCheckResultRenderer,
} from '@backstage-community/plugin-tech-insights-react';
import { MockScorecardTechInsightsClient } from '../../lib/MockScorecardTechInsightsClient';

// Tech Insights (PAT: portfolio linter scorecard) — client shared by the
// Component entity page card and plugins/scorecard (via useScorecardFact): the
// /scorecards tab, the Overview summary card and the leaderboard.
export function createTechInsightsApi({
  discoveryApi,
  identityApi,
  configApi,
}: {
  discoveryApi: typeof discoveryApiRef.T;
  identityApi: typeof identityApiRef.T;
  configApi: typeof configApiRef.T;
}) {
  const client = new TechInsightsClient({
    discoveryApi,
    identityApi,
    renderers: [
      {
        ...jsonRulesEngineCheckResultRenderer,
        description: result =>
          result.result
            ? `Repository passes the ${result.check.name} check.`
            : `Repository fails the ${result.check.name} check.`,
      },
    ],
  });

  // Local-dev-only escape hatch (see MockScorecardTechInsightsClient):
  // the scorecard's three facts are computed from Postgres tables that
  // don't exist locally, so without this the scorecard tabs only ever
  // show their empty state. Never set in app-config.k8s*.yaml.
  if (configApi.getOptionalBoolean('scorecard.mockFacts')) {
    return new MockScorecardTechInsightsClient(client);
  }
  return client;
}

// The extension name deliberately mirrors convertLegacyPlugin's id for this API
// (api:tech-insights/plugin.techinsights.service), so this module replaces the converted plugin's own
// extension instead of conflicting with it. Pinned by index.test.ts.
export const techInsightsModule = createFrontendModule({
  pluginId: 'tech-insights',
  extensions: [
    ApiBlueprint.make({
      name: 'plugin.techinsights.service',
      params: defineParams =>
        defineParams(
          createApiFactory({
            api: techInsightsApiRef,
            deps: {
              discoveryApi: discoveryApiRef,
              identityApi: identityApiRef,
              configApi: configApiRef,
            },
            factory: createTechInsightsApi,
          }),
        ),
    }),
  ],
});
